import React, {memo, useCallback, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useSelector} from 'react-redux';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {ConfigModel} from '../../../../store/control/state';
import {useHistory} from 'react-router-dom';
import {useQuestion} from '../../../../hooks/question';

const BACK_URL = '/settings';

export const AlarmEditScreen = memo(function AlarmEditScreen() {
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const question = useQuestion();

    const config = useSelector(state => state.control.config);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);

    const [homeTimer, setHomeTimer] = useState(config.alarm_settings.home_timer_seconds.toString());
    const [outsideTimer, setOutsideTimer] = useState(config.alarm_settings.outside_timer_seconds.toString());
    const [firstLevelAlarmCountdownDuration, setFirstLevelAlarmCountdownDuration] =
        useState(config.alarm.first_level_alarm_countdown_duration.toString());
    const [secondLevelAlarmCountdownDuration, setSecondLevelAlarmCountdownDuration] =
        useState(config.alarm.second_level_alarm_countdown_duration.toString());
    const [thirdLevelAlarmCountdownDuration, setThirdLevelAlarmCountdownDuration] =
        useState(config.alarm.third_level_alarm_countdown_duration.toString());
    const [reactivationDurationAfterReset, setReactivationDurationAfterReset] =
        useState(config.alarm.reactivation_duration_after_reset.toString());
    const [sirenIntervalOnDuration, setSirenIntervalOnDuration] =
        useState(config.alarm.siren_interval_on_duration.toString());
    const [sirenIntervalOffDuration, setSirenIntervalOffDuration] =
        useState(config.alarm.siren_interval_off_duration.toString());
    const [sirenRepeatCount, setSirenRepeatCount] =
        useState(config.alarm.siren_interval_repeat_count.toString());

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const save = useCallback(() => {
        const homeTimerValue = Number(homeTimer);
        if (isNaN(homeTimerValue) || homeTimerValue < 0) {
            failure('Evdeyim sayaç süresi geçersiz.');

            return;
        }

        const outsideTimerValue = Number(outsideTimer);
        if (isNaN(outsideTimerValue) || outsideTimerValue < 0) {
            failure('Dışardayım sayaç süresi geçersiz.');

            return;
        }

        const firstLevelAlarmCountdownDurationValue = Number(firstLevelAlarmCountdownDuration);
        if (isNaN(firstLevelAlarmCountdownDurationValue) || firstLevelAlarmCountdownDurationValue < 0) {
            failure('Alarm 1. seviye geri sayım süresi geçersiz.');

            return;
        }

        const secondLevelAlarmCountdownDurationValue = Number(secondLevelAlarmCountdownDuration);
        if (isNaN(secondLevelAlarmCountdownDurationValue) || secondLevelAlarmCountdownDurationValue < 0) {
            failure('Alarm 2. seviye geri sayım süresi geçersiz.');

            return;
        }

        const thirdLevelAlarmCountdownDurationValue = Number(thirdLevelAlarmCountdownDuration);
        if (isNaN(thirdLevelAlarmCountdownDurationValue) || thirdLevelAlarmCountdownDurationValue < 0) {
            failure('Alarm 3. seviye geri sayım süresi geçersiz.');

            return;
        }

        const reactivationDurationAfterResetValue = Number(reactivationDurationAfterReset);
        if (isNaN(reactivationDurationAfterResetValue) || reactivationDurationAfterResetValue < 0) {
            failure('Alarm reset sonrası tekrar aktifleşme süresi geçersiz.');

            return;
        }

        const sirenIntervalOnDurationValue = Number(sirenIntervalOnDuration);
        if (isNaN(sirenIntervalOnDurationValue) || sirenIntervalOnDurationValue < 0) {
            failure('Siren fasıla on süresi geçersiz.');

            return;
        }

        const sirenIntervalOffDurationValue = Number(sirenIntervalOffDuration);
        if (isNaN(sirenIntervalOffDurationValue) || sirenIntervalOffDurationValue < 0) {
            failure('Siren fasıla off süresi geçersiz.');

            return;
        }

        const sirenRepeatCountValue = Number(sirenRepeatCount);
        if (isNaN(sirenRepeatCountValue) || sirenRepeatCountValue < 0) {
            failure('Siren tekrar miktarı geçersiz.');

            return;
        }

        const configModel: ConfigModel = {
            ...config,
            alarm: {
                first_level_alarm_countdown_duration: firstLevelAlarmCountdownDurationValue,
                second_level_alarm_countdown_duration: secondLevelAlarmCountdownDurationValue,
                third_level_alarm_countdown_duration: thirdLevelAlarmCountdownDurationValue,
                reactivation_duration_after_reset: reactivationDurationAfterResetValue,
                siren_interval_on_duration: sirenIntervalOnDurationValue,
                siren_interval_off_duration: sirenIntervalOffDurationValue,
                siren_interval_repeat_count: sirenRepeatCountValue,
            },
            alarm_settings: {
                home_timer_seconds: homeTimerValue,
                outside_timer_seconds: outsideTimerValue,
            },
        };

        return rpc.updateConfigs(configModel).catch(failure);
    }, [
        config,
        homeTimer,
        outsideTimer,
        firstLevelAlarmCountdownDuration,
        secondLevelAlarmCountdownDuration,
        thirdLevelAlarmCountdownDuration,
        reactivationDurationAfterReset,
        sirenIntervalOnDuration,
        sirenIntervalOffDuration,
        sirenRepeatCount,
        failure,
        rpc,
    ]);

    const onSavePressed = useCallback(() => {
        save()
            .then(() => {
                success('Başarılı bir şekilde düzenlendi.');
                onBackPressed();
            });
    }, [
        save,
        success,
        onBackPressed,
    ]);

    const onApplyPressed = useCallback(() => {
        const count = bridgeDeviceList.length;
        question(`${count.toString()} cihaza bu ayarlar uygulanacaktır. Devam edilsin mi?`, () => {
            save()
                .then(async () => {
                    for (const bridgeDevice of bridgeDeviceList) {
                        await rpc.updateBridgeDevice({
                            ...bridgeDevice, alarm_config: {
                                ...bridgeDevice.alarm_config,
                                first_level_alarm_countdown_duration: Number(firstLevelAlarmCountdownDuration),
                                second_level_alarm_countdown_duration: Number(secondLevelAlarmCountdownDuration),
                                third_level_alarm_countdown_duration: Number(thirdLevelAlarmCountdownDuration),
                                reactivation_duration_after_reset: Number(reactivationDurationAfterReset),
                                siren_interval_on_duration: Number(sirenIntervalOnDuration),
                                siren_interval_off_duration: Number(sirenIntervalOffDuration),
                                siren_interval_repeat_count: Number(sirenRepeatCount),
                            },
                        });
                    }
                })
                .then(() => {
                    success('Başarılı bir şekilde düzenlendi.');
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [
        firstLevelAlarmCountdownDuration,
        secondLevelAlarmCountdownDuration,
        thirdLevelAlarmCountdownDuration,
        reactivationDurationAfterReset,
        sirenIntervalOnDuration,
        sirenIntervalOffDuration,
        sirenRepeatCount,
        save,
        rpc,
        question,
        bridgeDeviceList,
        success,
        onBackPressed,
        failure,
    ]);

    const buttons = [
        {
            text: 'UYGULA',
            className: 'blue',
            onClick: onApplyPressed,
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        },
    ];

    const onBlur = useCallback(() => {
        setActiveNumpadInput(null);
    }, [setActiveNumpadInput]);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <div className='line'>
                            <span>ALARM GERİ SAYIM SÜRELERİ</span>
                        </div>
                        <SettingInputComponent
                            placeholder='1. Seviye'
                            label='1. Seviye'
                            value={firstLevelAlarmCountdownDuration}
                            setValue={setFirstLevelAlarmCountdownDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder='2. Seviye'
                            label='2. Seviye'
                            value={secondLevelAlarmCountdownDuration}
                            setValue={setSecondLevelAlarmCountdownDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder='3. Seviye'
                            label='3. Seviye'
                            value={thirdLevelAlarmCountdownDuration}
                            setValue={setThirdLevelAlarmCountdownDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <div className='line'>
                            <span>ALARM RESET SONRASI DEVREYE GİRME</span>
                        </div>
                        <SettingInputComponent
                            placeholder='Devreye girme süresi'
                            label='Devreye Gr. Süresi'
                            value={reactivationDurationAfterReset}
                            setValue={setReactivationDurationAfterReset}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                    </div>
                    <div className='content-wrapper'>
                        <div className='line'>
                            <span>SİREN FASILA</span>
                        </div>
                        <SettingInputComponent
                            placeholder='Fasıla on süresi'
                            label='Fasıla On Süresi'
                            value={sirenIntervalOnDuration}
                            setValue={setSirenIntervalOnDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder='Fasıla off süresi'
                            label='Fasıla Off Süresi'
                            value={sirenIntervalOffDuration}
                            setValue={setSirenIntervalOffDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder='Fasıla tekrar miktarı'
                            label='Fasıla Tekrar Miktarı'
                            value={sirenRepeatCount}
                            setValue={setSirenRepeatCount}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <div className='line'>
                            <span>ALARM KURULUM SAYAÇ SÜRELERİ</span>
                        </div>
                        <SettingInputComponent
                            placeholder='Evdeyim'
                            label='Evdeyim'
                            value={homeTimer}
                            setValue={setHomeTimer}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder='Dışardayım'
                            label='Dışardayım'
                            value={outsideTimer}
                            setValue={setOutsideTimer}
                            setActiveInput={setActiveNumpadInput}
                            tempValue='0'
                            onBlur={onBlur}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
